import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { NoteDeleteSchema, NoteMutationSchema, NoteUpdateSchema } from './schemas/notes-mutation-schema';
import type { NoteContextTypeSchema, NoteQueryAllSchema, NoteQuerySchema } from './schemas/notes-query-schema';
import { noteQuerySchema } from './schemas/notes-query-schema';

// Get all notes for a specific context (client or contact)
export function getNotesQuery(contextType: NoteContextTypeSchema, contextId: string) {
  return {
    queryKey: ['NOTES', contextType, contextId],
    queryFn: async (): Promise<NoteQueryAllSchema> => {
      const res = await apiClient.get<NoteQueryAllSchema>({
        endpoint: `/api/v1/notes`,
        params: {
          include: 'user.team_member.profile_image,context',
          'filter[advance_filtering]': `match[context_type]=${contextType};match[context_id]=${contextId}`,
        },
        responseSchema: noteQuerySchema,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function useGetNotes(contextType: NoteContextTypeSchema, contextId: string) {
  return useSuspenseQuery(getNotesQuery(contextType, contextId));
}

// Create a new note
export const useCreateNoteMutation = () =>
  useMutation<{ data: NoteQuerySchema }, ApiError, NoteMutationSchema>({
    mutationFn: async (payload): Promise<{ data: NoteQuerySchema }> => {
      return apiClient.request({
        endpoint: `/api/v1/notes`,
        payload,
      });
    },
  });

// Delete a note
export const useDeleteNoteMutation = (contextType: NoteContextTypeSchema, contextId: string) =>
  useMutation<unknown, ApiError, NoteDeleteSchema>({
    mutationFn: async ({ id }): Promise<unknown> => {
      return apiClient.request({
        endpoint: `/api/v1/notes/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NOTES', contextType, contextId] });
    },
  });

// Update a note
export const useUpdateNoteMutation = (contextType: NoteContextTypeSchema, contextId: string) =>
  useMutation<{ data: NoteQuerySchema }, ApiError, NoteUpdateSchema>({
    mutationFn: async ({ id, message }): Promise<{ data: NoteQuerySchema }> => {
      return apiClient.request({
        endpoint: `/api/v1/notes/${id}`,
        method: 'PUT',
        payload: { message },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['NOTES', contextType, contextId] });
    },
  });

// Invalidate notes queries
export const invalidateNotesQuery = async (contextType: NoteContextTypeSchema, contextId: string) => {
  await queryClient.invalidateQueries({ queryKey: ['NOTES', contextType, contextId] });
};
